import type { LiveShowActivity } from "./live-activities";

export const SHOW_NIGHT_LIVE_ACTIVITY = "ShowNight";

export const PHONE_LIVE_ACTIVITY_DISMISSAL_MS = 2 * 60 * 60 * 1000;

export const PHONE_LIVE_ACTIVITY_MESSAGE = "award-party:live-activity";

export type PhoneLiveActivityPhase = "waiting" | "live" | "final";

export type PhoneLiveActivityTokenKind = "ios_start" | "ios_update" | "android";

export type PhoneLiveActivityPushEvent = "start" | "update" | "end" | "skip";

export interface ShowNightActivityProps {
  partyId: string;
  title: string;
  headline: string;
  detail: string;
  status: string;
  trailing: string;
}

export type PhoneLiveActivityPart =
  | { key: "title" | "waitingTitle" | "waitingBody" | "finalTitle" | "noLead" | "gotIt" | "missed" | "noPick" }
  | { key: "leadOpened" | "leadChanged" | "leadHeld"; name: string }
  | { key: "leadOpenedTie" | "leadChangedTie" | "leadHeldTie"; names: string }
  | { key: "lockWinner"; category: string; winner: string }
  | { key: "lockStatus"; you: string; result: string }
  | { key: "youRank"; rank: number; score: number }
  | { key: "youRankUp" | "youRankDown"; rank: number; previousRank: number }
  | { key: "rankLabel"; rank: number };

export interface PhoneLiveActivityScript {
  headline: PhoneLiveActivityPart;
  detail: PhoneLiveActivityPart | null;
  status: PhoneLiveActivityPart | null;
  you: PhoneLiveActivityPart | null;
  trailing: PhoneLiveActivityPart | null;
}

export interface PhoneLiveActivityCommand {
  action: "sync" | "end";
  partyId: string;
  url: string;
  props: ShowNightActivityProps;
}

const CLIP = {
  title: 40,
  headline: 80,
  detail: 120,
  status: 140,
  trailing: 16,
} as const;

export function formatPhoneLiveActivityNames(
  names: readonly string[],
  locale: string,
  andOthers: (shown: string, count: number) => string,
): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length <= 2) {
    return new Intl.ListFormat(locale, { type: "conjunction" }).format([
      ...names,
    ]);
  }
  return andOthers(names.slice(0, 2).join(", "), names.length - 2);
}

export function showNightPhase(
  announcedCount: number,
  categoryCount: number,
): PhoneLiveActivityPhase {
  if (categoryCount > 0 && announcedCount >= categoryCount) {
    return "final";
  }
  if (announcedCount > 0) {
    return "live";
  }
  return "waiting";
}

export function phoneLiveActivityPushEvent(input: {
  kind: PhoneLiveActivityTokenKind;
  phase: PhoneLiveActivityPhase;
}): PhoneLiveActivityPushEvent {
  if (input.phase === "waiting") {
    return input.kind === "ios_update" ? "update" : "skip";
  }
  if (input.kind === "ios_start") {
    return "start";
  }
  if (input.kind === "ios_update" && input.phase === "final") {
    return "end";
  }
  return "update";
}

export function phoneLiveActivityUrl(
  origin: string,
  partyId: string,
): string | null {
  const id = partyId.trim();
  if (!id || id.length > 80 || !/^[\w-]+$/.test(id)) {
    return null;
  }
  let base: URL;
  try {
    base = new URL(origin);
  } catch {
    return null;
  }
  if (base.protocol !== "https:" && base.protocol !== "http:") {
    return null;
  }
  return new URL(`/parties/${id}`, base).toString();
}

export function buildPhoneLiveActivityScript(
  phase: PhoneLiveActivityPhase,
  activity: LiveShowActivity | null,
  formatNames: (names: readonly string[]) => string,
): PhoneLiveActivityScript {
  if (!activity || phase === "waiting") {
    if (phase === "final") {
      return {
        headline: { key: "finalTitle" },
        detail: null,
        status: null,
        you: null,
        trailing: null,
      };
    }
    return {
      headline: { key: "waitingTitle" },
      detail: { key: "waitingBody" },
      status: null,
      you: null,
      trailing: null,
    };
  }

  return {
    headline: leadPart(activity, formatNames),
    detail: {
      key: "lockWinner",
      category: activity.categoryName,
      winner: activity.winnerName,
    },
    status: statusPart(activity),
    you: youPart(activity),
    trailing: activity.currentUser
      ? { key: "rankLabel", rank: activity.currentUser.rank }
      : null,
  };
}

export function renderShowNightActivityProps(
  partyId: string,
  partyName: string,
  script: PhoneLiveActivityScript,
  translate: (part: PhoneLiveActivityPart) => string,
): ShowNightActivityProps {
  const you = script.you ? translate(script.you).trim() : "";
  const result = script.status ? translate(script.status).trim() : "";
  const status =
    you && result
      ? translate({ key: "lockStatus", you, result })
      : you || result;
  const title = partyName.trim() || translate({ key: "title" });

  return {
    partyId,
    title: clip(title, CLIP.title),
    headline: clip(translate(script.headline), CLIP.headline),
    detail: clip(script.detail ? translate(script.detail) : "", CLIP.detail),
    status: clip(status, CLIP.status),
    trailing: clip(script.trailing ? translate(script.trailing) : "", CLIP.trailing),
  };
}

export function buildApnsLiveActivityPayload(input: {
  name?: string;
  event: "start" | "update" | "end";
  props: ShowNightActivityProps;
  timestampSeconds: number;
  dismissalDateSeconds?: number;
}): { aps: Record<string, unknown> } {
  const aps: Record<string, unknown> = {
    timestamp: input.timestampSeconds,
    event: input.event,
    "content-state": {
      name: input.name ?? SHOW_NIGHT_LIVE_ACTIVITY,
      props: JSON.stringify(input.props),
    },
  };

  if (input.event === "start") {
    aps["attributes-type"] = "LiveActivityAttributes";
    aps.attributes = {};
    aps["input-push-token"] = 1;
    aps.alert = {
      title: input.props.headline,
      body: input.props.detail || input.props.status || input.props.title,
    };
  }

  if (input.event === "end" && input.dismissalDateSeconds !== undefined) {
    aps["dismissal-date"] = input.dismissalDateSeconds;
  }

  return { aps };
}

export function apnsLiveActivityHeaders(input: {
  authorization: string;
  bundleId: string;
}): Record<string, string> {
  return {
    authorization: input.authorization,
    "apns-topic": `${input.bundleId}.push-type.liveactivity`,
    "apns-push-type": "liveactivity",
    "apns-priority": "10",
  };
}

export interface ExpoShowNightPush {
  to: string;
  title: string;
  body: string;
  data: { url: string };
  priority: "high";
  channelId: "show-night";
  collapseId: string;
}

export function buildExpoShowNightPush(input: {
  to: string;
  props: ShowNightActivityProps;
  url: string;
}): ExpoShowNightPush {
  const body = [input.props.detail, input.props.status]
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join("\n");

  return {
    to: input.to,
    title: input.props.headline || input.props.title,
    body: body || input.props.title,
    data: { url: input.url },
    priority: "high",
    channelId: "show-night",
    collapseId: `show-night-${input.props.partyId}`,
  };
}

function leadPart(
  activity: LiveShowActivity,
  formatNames: (names: readonly string[]) => string,
): PhoneLiveActivityPart {
  if (activity.leaders.length === 0) {
    return { key: "noLead" };
  }
  if (activity.leaders.length === 1) {
    const name = activity.leaders[0]?.name ?? "";
    if (activity.leadChange === "opened") {
      return { key: "leadOpened", name };
    }
    if (activity.leadChange === "changed") {
      return { key: "leadChanged", name };
    }
    return { key: "leadHeld", name };
  }
  const names = formatNames(activity.leaders.map((leader) => leader.name));
  if (activity.leadChange === "opened") {
    return { key: "leadOpenedTie", names };
  }
  if (activity.leadChange === "changed") {
    return { key: "leadChangedTie", names };
  }
  return { key: "leadHeldTie", names };
}

function statusPart(activity: LiveShowActivity): PhoneLiveActivityPart | null {
  if (!activity.currentUser) {
    return null;
  }
  if (activity.currentUserCorrect === true) {
    return { key: "gotIt" };
  }
  if (activity.currentUserCorrect === false) {
    return { key: "missed" };
  }
  return { key: "noPick" };
}

function youPart(activity: LiveShowActivity): PhoneLiveActivityPart | null {
  const player = activity.currentUser;
  if (!player) {
    return null;
  }
  if (player.previousRank !== null && player.previousRank > player.rank) {
    return {
      key: "youRankUp",
      rank: player.rank,
      previousRank: player.previousRank,
    };
  }
  if (player.previousRank !== null && player.previousRank < player.rank) {
    return {
      key: "youRankDown",
      rank: player.rank,
      previousRank: player.previousRank,
    };
  }
  return { key: "youRank", rank: player.rank, score: player.score };
}

function clip(value: string, max: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= max) {
    return trimmed;
  }
  return `${trimmed.slice(0, max - 3).trimEnd()}...`;
}
